import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wrench, PaintBucket, HelpCircle, ArrowLeft, ArrowRight, Upload, X,
  CheckCircle2, Phone,
} from "lucide-react";
import { Kicker } from "@/components/Reveal";
import api from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];
const TYPE_ICONS = [Wrench, PaintBucket, HelpCircle];
const TYPE_KEYS = ["Handyman Repair", "Remodeling", "Other"];

export default function Estimate({ lang }) {
  const { t } = useTranslation();
  const prefix = lang === "es" ? "/es" : "";
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const [data, setData] = useState({
    project_type: "", rooms: "", scope: "", timeline: "", budget: "",
    name: "", email: "", phone: "", address: "", zip_code: "",
  });
  const [photos, setPhotos] = useState([]);
  const [touched, setTouched] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const go = (n) => { setDir(n > step ? 1 : -1); setStep(n); setTouched(false); setError(""); window.scrollTo({ top: 0 }); };

  const valid = () => {
    if (step === 0) return !!data.project_type;
    if (step === 1) return !!data.rooms.trim() && !!data.scope.trim() && !!data.timeline && !!data.budget;
    if (step === 3) return !!data.name.trim() && /.+@.+\..+/.test(data.email) && !!data.phone.trim();
    return true;
  };

  const next = () => { if (valid()) go(step + 1); else setTouched(true); };

  const addPhotos = (files) => {
    const list = Array.from(files || []);
    setPhotos((p) => [...p, ...list].slice(0, 6));
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const fd = new FormData();
      Object.entries(data).forEach(([k, v]) => fd.append(k, v));
      fd.append("lang", lang);
      photos.forEach((p) => fd.append("photos", p));
      await api.post("/estimates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (e) {
      setError(t("estimate.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const typeOptions = t("estimate.s1.options", { returnObjects: true });
  const timelines = t("estimate.s2.timelines", { returnObjects: true });
  const budgets = t("estimate.s2.budgets", { returnObjects: true });
  const typeLabel = typeOptions[TYPE_KEYS.indexOf(data.project_type)] || data.project_type;

  if (done) {
    return (
      <section data-testid="estimate-confirmation" className="min-h-screen pt-40 pb-24 px-6 md:px-12 bg-navy text-offwhite flex items-center">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 15 }}>
            <CheckCircle2 size={72} className="text-orange mx-auto" strokeWidth={1.4} />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8, ease: EASE }}
            className="mt-8 font-heading font-black uppercase tracking-tighter text-4xl sm:text-6xl"
          >
            {t("estimate.done.title")}
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-6 text-offwhite/70 text-lg leading-relaxed">
            {t("estimate.done.sub")}
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="tel:17867746692" data-testid="confirmation-call" className="inline-flex items-center gap-3 border border-offwhite/30 px-8 py-4 text-sm font-bold uppercase tracking-widest hover:border-orange hover:text-orange transition-colors duration-200">
              <Phone size={16} /> 786-774-6692
            </a>
            <Link to={prefix || "/"} data-testid="confirmation-home" className="inline-flex items-center gap-3 bg-orange hover:bg-orange-bright text-white px-8 py-4 text-sm font-bold uppercase tracking-widest transition-[background-color,transform] duration-200 hover:scale-105 active:scale-95">
              {t("estimate.done.home")}
            </Link>
          </motion.div>
          <p className="mt-8 text-offwhite/40 text-sm">{t("estimate.done.emergency")}</p>
        </div>
      </section>
    );
  }

  const variants = {
    enter: (d) => ({ x: d * 60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d * -60, opacity: 0 }),
  };

  const inputCls = "w-full border border-sandline bg-white px-4 py-3.5 text-navy focus:outline-none focus:border-orange transition-colors duration-200";
  const labelCls = "block text-xs font-bold uppercase tracking-[0.2em] text-slateblue mb-2";
  const errCls = "mt-1 text-xs text-red-600";

  const reviewRows = [
    { label: t("estimate.s5.project"), value: typeLabel, step: 0 },
    { label: t("estimate.s2.rooms"), value: data.rooms, step: 1 },
    { label: t("estimate.s2.scope"), value: data.scope, step: 1 },
    { label: t("estimate.s2.timeline"), value: data.timeline, step: 1 },
    { label: t("estimate.s2.budget"), value: data.budget, step: 1 },
    { label: t("estimate.s5.contact"), value: `${data.name} · ${data.email} · ${data.phone}`, step: 3 },
    { label: t("estimate.s4.address"), value: `${data.address} ${data.zip_code}`.trim() || "—", step: 3 },
    { label: t("estimate.s5.photos"), value: photos.length ? `${photos.length}` : t("estimate.s5.none"), step: 2 },
  ];

  return (
    <section data-testid="estimate-page" className="min-h-screen pt-32 pb-24 px-6 md:px-12">
      <div className="max-w-3xl mx-auto">
        <div className="mb-12">
          <Kicker>{t("estimate.kicker")}</Kicker>
          <h1 className="mt-4 font-heading font-black uppercase tracking-tighter text-4xl sm:text-5xl">{t("estimate.title")}</h1>
          <p className="mt-4 text-slateblue">{t("estimate.sub")}</p>
        </div>

        <div className="mb-10" data-testid="estimate-progress">
          <div className="flex justify-between text-xs font-bold uppercase tracking-widest text-slateblue mb-3">
            <span>{t("estimate.step")} {step + 1} {t("estimate.of")} 5</span>
            <span>{Math.round(((step + 1) / 5) * 100)}%</span>
          </div>
          <div className="h-1 bg-offwhite-dark overflow-hidden">
            <motion.div
              animate={{ width: `${((step + 1) / 5) * 100}%` }}
              transition={{ duration: 0.5, ease: EASE }}
              className="h-full bg-orange"
            />
          </div>
        </div>

        <div className="relative min-h-[420px]">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={step}
              custom={dir}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.45, ease: EASE }}
            >
              {step === 0 && (
                <div data-testid="estimate-step-1">
                  <h2 className="font-heading font-bold text-2xl tracking-tight mb-8">{t("estimate.s1.title")}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {typeOptions.map((opt, i) => {
                      const Icon = TYPE_ICONS[i];
                      const selected = data.project_type === TYPE_KEYS[i];
                      return (
                        <button
                          key={i}
                          onClick={() => set("project_type", TYPE_KEYS[i])}
                          data-testid={`estimate-type-${TYPE_KEYS[i].toLowerCase().replace(/\s/g, "-")}`}
                          className={`flex items-center gap-4 p-6 border text-left transition-[border-color,background-color,transform] duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                            selected ? "border-orange bg-orange/5" : "border-sandline bg-white hover:border-navy/40"
                          }`}
                        >
                          <Icon size={26} strokeWidth={1.6} className={selected ? "text-orange" : "text-navy"} />
                          <span className="font-heading font-bold">{opt}</span>
                        </button>
                      );
                    })}
                  </div>
                  {touched && !data.project_type && <p className={errCls} data-testid="step1-error">{t("estimate.required")}</p>}
                </div>
              )}

              {step === 1 && (
                <div data-testid="estimate-step-2">
                  <h2 className="font-heading font-bold text-2xl tracking-tight mb-8">{t("estimate.s2.title")}</h2>
                  <div className="space-y-6">
                    <div>
                      <label className={labelCls}>{t("estimate.s2.rooms")} *</label>
                      <input value={data.rooms} onChange={(e) => set("rooms", e.target.value)} placeholder={t("estimate.s2.roomsPh")} data-testid="estimate-rooms-input" className={inputCls} />
                      {touched && !data.rooms.trim() && <p className={errCls}>{t("estimate.required")}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>{t("estimate.s2.scope")} *</label>
                      <textarea rows={4} value={data.scope} onChange={(e) => set("scope", e.target.value)} placeholder={t("estimate.s2.scopePh")} data-testid="estimate-scope-input" className={`${inputCls} resize-none`} />
                      {touched && !data.scope.trim() && <p className={errCls}>{t("estimate.required")}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>{t("estimate.s2.timeline")} *</label>
                      <div className="flex flex-wrap gap-2">
                        {timelines.map((opt) => (
                          <button key={opt} onClick={() => set("timeline", opt)} data-testid={`estimate-timeline-${slug(opt)}`}
                            className={`px-4 py-2.5 text-sm font-semibold border transition-colors duration-200 ${data.timeline === opt ? "border-orange bg-orange/5 text-orange" : "border-sandline bg-white text-navy hover:border-navy/40"}`}>
                            {opt}
                          </button>
                        ))}
                      </div>
                      {touched && !data.timeline && <p className={errCls}>{t("estimate.required")}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>{t("estimate.s2.budget")} *</label>
                      <div className="flex flex-wrap gap-2">
                        {budgets.map((opt) => (
                          <button key={opt} onClick={() => set("budget", opt)} data-testid={`estimate-budget-${slug(opt)}`}
                            className={`px-4 py-2.5 text-sm font-semibold border transition-colors duration-200 ${data.budget === opt ? "border-orange bg-orange/5 text-orange" : "border-sandline bg-white text-navy hover:border-navy/40"}`}>
                            {opt}
                          </button>
                        ))}
                      </div>
                      {touched && !data.budget && <p className={errCls}>{t("estimate.required")}</p>}
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div data-testid="estimate-step-3">
                  <h2 className="font-heading font-bold text-2xl tracking-tight mb-4">{t("estimate.s3.title")}</h2>
                  <p className="text-slateblue mb-8">{t("estimate.s3.desc")}</p>
                  <button
                    onClick={() => fileRef.current?.click()}
                    data-testid="estimate-photo-upload"
                    className="w-full border-2 border-dashed border-sandline hover:border-orange bg-white p-12 flex flex-col items-center gap-3 transition-colors duration-200"
                  >
                    <Upload size={30} className="text-orange" strokeWidth={1.6} />
                    <span className="font-bold">{t("estimate.s3.upload")}</span>
                    <span className="text-xs text-slateblue">{t("estimate.s3.hint")}</span>
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="estimate-photo-input"
                    onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
                  {photos.length > 0 && (
                    <div className="mt-6 grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="estimate-photo-previews">
                      {photos.map((p, i) => (
                        <div key={i} className="relative group">
                          <img src={URL.createObjectURL(p)} alt="" className="w-full aspect-square object-cover border border-sandline" />
                          <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} data-testid={`estimate-photo-remove-${i}`}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-navy text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {step === 3 && (
                <div data-testid="estimate-step-4">
                  <h2 className="font-heading font-bold text-2xl tracking-tight mb-8">{t("estimate.s4.title")}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className={labelCls}>{t("estimate.s4.name")} *</label>
                      <input value={data.name} onChange={(e) => set("name", e.target.value)} data-testid="estimate-name-input" className={inputCls} />
                      {touched && !data.name.trim() && <p className={errCls}>{t("estimate.required")}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>{t("estimate.s4.phone")} *</label>
                      <input value={data.phone} onChange={(e) => set("phone", e.target.value)} data-testid="estimate-phone-input" className={inputCls} />
                      {touched && !data.phone.trim() && <p className={errCls}>{t("estimate.required")}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls}>{t("estimate.s4.email")} *</label>
                      <input type="email" value={data.email} onChange={(e) => set("email", e.target.value)} data-testid="estimate-email-input" className={inputCls} />
                      {touched && !/.+@.+\..+/.test(data.email) && <p className={errCls}>{t("estimate.required")}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>{t("estimate.s4.address")}</label>
                      <input value={data.address} onChange={(e) => set("address", e.target.value)} data-testid="estimate-address-input" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>{t("estimate.s4.zip")}</label>
                      <input value={data.zip_code} onChange={(e) => set("zip_code", e.target.value)} data-testid="estimate-zip-input" className={inputCls} />
                    </div>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div data-testid="estimate-step-5">
                  <h2 className="font-heading font-bold text-2xl tracking-tight mb-8">{t("estimate.s5.title")}</h2>
                  <div className="bg-white border border-sandline divide-y divide-sandline">
                    {reviewRows.map((r, i) => (
                      <div key={i} className="flex items-start justify-between gap-6 p-5">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slateblue">{r.label}</p>
                          <p className="mt-1 font-medium text-navy whitespace-pre-line">{r.value}</p>
                        </div>
                        <button onClick={() => go(r.step)} data-testid={`review-edit-${i}`} className="text-xs font-bold uppercase tracking-widest text-orange hover:text-orange-bright shrink-0 transition-colors duration-200">
                          {t("estimate.s5.edit")}
                        </button>
                      </div>
                    ))}
                  </div>
                  {error && <p className="mt-4 text-sm text-red-600" data-testid="estimate-submit-error">{error}</p>}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-12 flex items-center justify-between">
          <button
            onClick={() => go(step - 1)}
            disabled={step === 0}
            data-testid="estimate-back-btn"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-navy disabled:opacity-30 hover:text-orange transition-colors duration-200"
          >
            <ArrowLeft size={16} /> {t("estimate.back")}
          </button>
          {step < 4 ? (
            <button
              onClick={next}
              data-testid="estimate-next-btn"
              className="inline-flex items-center gap-3 bg-navy hover:bg-navy-800 text-white font-bold px-8 py-4 text-sm uppercase tracking-widest transition-[background-color,transform] duration-200 hover:scale-105 active:scale-95"
            >
              {t("estimate.next")} <ArrowRight size={16} />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="estimate-submit-btn"
              className="inline-flex items-center gap-3 bg-orange hover:bg-orange-bright disabled:opacity-60 text-white font-bold px-8 py-4 text-sm uppercase tracking-widest transition-[background-color,transform] duration-200 hover:scale-105 active:scale-95"
            >
              {submitting ? t("estimate.submitting") : t("estimate.submit")} <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
